import { useCallback, useEffect, useState } from 'react';
import { useToggle } from '../../../../../../hooks/useToggle';
import { transformFromPostingDetailsEntityToFormData } from '../../../PostPermanentJobV2/utils/PostingDetailsMapper';
import { CompensationType, JobStatus } from '../../../shared/constants';
import { isSubProfession } from '../../../shared/utils';
import { UserSubProfession } from '../../../../../../enums/UserProfession';
import { useUpdateJobPosting } from '../../../../../../domain/PermanentJobs';
import { showSuccessMessage } from '../../../../../../utils';

/**
 * Custom hook for managing job posting edit form state and mutations
 * Handles form editing states for different sections (general info, compensation, schedule, description)
 * and provides form submission functionality
 *
 * @param {Object} params - Hook parameters
 * @param {Object} params.currentJobPosting - The current job posting data to edit
 * @param {string} params.currentJobPosting.officeId - Office ID
 * @param {string} params.currentJobPosting.officeName - Office name
 *
 * @returns {Object} Form state and handlers
 * @returns {Object} return.formEdits - Form edit state controls
 * @returns {Object} return.formEdits.controls - Toggle controls for each form section
 * @returns {Object} return.formEdits.controls.generalJobInfo - Toggle controls for general job info section
 * @returns {Object} return.formEdits.controls.compensationBenefits - Toggle controls for compensation & benefits section
 * @returns {Object} return.formEdits.controls.schedule - Toggle controls for schedule section
 * @returns {Object} return.formEdits.controls.jobDescription - Toggle controls for job description section
 * @returns {boolean} return.formEdits.isEditGeneralJobInfo - Whether general job info section is in edit mode
 * @returns {boolean} return.formEdits.isEditCompensationBenefits - Whether compensation & benefits section is in edit mode
 * @returns {boolean} return.formEdits.isEditSchedule - Whether schedule section is in edit mode
 * @returns {boolean} return.formEdits.isEditJobDescription - Whether job description section is in edit mode
 * @returns {Object} return.formValues - Current form values state
 * @returns {boolean} return.isPending - Whether a mutation is currently pending
 * @returns {Function} return.doUpdateJobPosting - Mutation function to update job posting with custom payload
 * @returns {Function} return.doUpdateRecruiter - Mutation function to update job posting recruiter. Automatically shows success message
 * @returns {Function} return.doUpdateStatus - Mutation function to update job posting status. Shows different messages for closed vs other statuses
 * @returns {Function} return.handleFormChange - Handler for form field changes
 */
export const useEditPostingForm = ({ currentJobPosting }) => {
  const [isEditCompensationBenefits, controlsCompensationBenefits] = useToggle();
  const [isEditGeneralJobInfo, controlsGeneralJobInfo] = useToggle();
  const [isEditJobDescription, controlsJobDescription] = useToggle();
  const [isEditSchedule, controlsSchedule] = useToggle();

  const [formValues, setFormValues] = useState();

  const { mutate, isPending } = useUpdateJobPosting({
    onSuccess() {
      controlsCompensationBenefits.setOff();
      controlsGeneralJobInfo.setOff();
      controlsJobDescription.setOff();
      controlsSchedule.setOff();
    },
  });

  /**
   * Prefills the form state based on the current job posting
   * Runs when currentJobPosting changes to initialize form values
   */
  useEffect(() => {
    if (currentJobPosting) {
      setFormValues({
        ...transformFromPostingDetailsEntityToFormData(currentJobPosting),
        office: {
          id: currentJobPosting?.officeId,
          officeName: currentJobPosting?.officeName,
        },
      });
    }
  }, [currentJobPosting]);

  /**
   * Resets form values to defaults when any section exits edit mode
   * This ensures unsaved changes are discarded when user cancels editing
   */
  useEffect(() => {
    if (
      !isEditCompensationBenefits ||
      !isEditGeneralJobInfo ||
      !isEditJobDescription ||
      !isEditSchedule
    ) {
      setFormValues({
        ...transformFromPostingDetailsEntityToFormData(currentJobPosting),
        office: {
          id: currentJobPosting?.officeId,
          officeName: currentJobPosting?.officeName,
        },
      });
    }
  }, [
    currentJobPosting,
    isEditCompensationBenefits,
    isEditGeneralJobInfo,
    isEditJobDescription,
    isEditSchedule,
  ]);

  const doUpdateJobPosting = useCallback(
    (payload) => {
      mutate(payload, {
        onSuccess() {
          showSuccessMessage({ message: 'Job posting updated successfully' });
        },
      });
    },
    [mutate],
  );

  /**
   * Updates the recruiter assigned to the job posting
   * Merges current form values with the new recruiter email and triggers mutation
   *
   * @param {string} jobPostingId - The ID of the job posting to update
   * @param {string} email - The email address of the new recruiter
   */
  const doUpdateRecruiter = useCallback(
    (jobPostingId, email) => {
      mutate(
        {
          ...formValues,
          postingId: jobPostingId,
          recruiterEmail: email,
        },
        {
          onSuccess() {
            showSuccessMessage({ message: 'Recruiter updated successfully' });
          },
        },
      );
    },
    [formValues, mutate],
  );

  /**
   * Updates the status of the job posting
   * Merges current form values with the new status and triggers mutation
   * Shows different success messages based on the status being set
   *
   * @param {string} jobPostingId - The ID of the job posting to update
   * @param {import('../../../shared/constants').JobStatus} status - The new status for the job posting ('active' | 'closed')
   */
  const doUpdateStatus = useCallback(
    (jobPostingId, status) => {
      mutate(
        {
          ...formValues,
          postingId: jobPostingId,
          status,
        },
        {
          onSuccess() {
            if (status === JobStatus.CLOSED) {
              showSuccessMessage({
                message: 'Job Successfully Closed and Candidates Have Been Archived',
              });
            } else {
              showSuccessMessage({ message: 'Job status updated successfully' });
            }
          },
        },
      );
    },
    [formValues, mutate],
  );

  /**
   * Handles form field changes with special logic for profession and compensation types
   *
   * @param {string} field - The field name to update
   * @param {*} value - The new value for the field
   *
   * Special handling:
   * - professionType: Automatically splits sub-professions into professionType and subProfession fields
   * - compensationType: Clears maxRate when switching to FIXED compensation type
   */
  const handleFormChange = useCallback((field, value) => {
    if (field === 'professionType') {
      if (isSubProfession(value)) {
        setFormValues((prev) => ({
          ...prev,
          professionType: UserSubProfession.getProfession(value),
          subProfession: value,
        }));
      } else {
        setFormValues((prev) => ({
          ...prev,
          professionType: value,
          subProfession: '',
        }));
      }
      return;
    }

    if (field === 'compensationType' && value === CompensationType.FIXED) {
      setFormValues((prev) => ({
        ...prev,
        [field]: value,
        maxRate: null,
      }));
      return;
    }

    if (field === 'jobDescription') {
      setFormValues((prev) => ({
        ...prev,
        [field]: {
          raw: value?.raw,
          text: value?.text,
        },
      }));
    }

    setFormValues((prev) => ({
      ...prev,
      [field]: value,
    }));
  }, []);

  return {
    formEdits: {
      controls: {
        generalJobInfo: controlsGeneralJobInfo,
        compensationBenefits: controlsCompensationBenefits,
        schedule: controlsSchedule,
        jobDescription: controlsJobDescription,
      },
      isEditGeneralJobInfo,
      isEditCompensationBenefits,
      isEditSchedule,
      isEditJobDescription,
    },
    formValues,
    isPending,
    doUpdateJobPosting,
    doUpdateRecruiter,
    doUpdateStatus,
    handleFormChange,
  };
};
